/**
 * Packet identifiers.
 *
 * Values are permanent. Removing a packet leaves a gap rather than renumbering the
 * ones after it, because a client from before an update stays connected until it
 * reloads and would otherwise read every following packet as the wrong thing.
 */

/**
 * Bumped whenever the meaning of an existing packet changes. The server refuses a
 * client that does not match, and the client shows "reload to continue" instead of
 * silently misreading the world.
 */
export const PROTOCOL_VERSION = 89;

/** Client to server. */
export const ClientOp = {
  /** First packet on any connection. Carries the protocol version and session token. */
  Handshake: 1,
  /** Keeps the connection alive and measures round trip time. */
  Ping: 2,
  /** A batch of 20Hz input frames, so a dropped packet does not lose movement. */
  InputFrames: 3,
  /** Click to move. Carries a destination tile, not a position. */
  MoveTo: 4,
  /** Toggle run. */
  SetRunning: 5,
  /** Interact with an entity or object: the intent, never the result. */
  Interact: 6,
  /** Public chat. */
  Chat: 7,
  /** Client changed a setting the server needs to know about, such as camera mode. */
  UpdateSettings: 8,
  // 9 was a follow request. Walking after another entity is steering, so it goes
  // through the ordinary input frames and needs no packet of its own. The number is
  // left unused rather than reassigned.
  /** A message to one named player. */
  PrivateMessage: 10,
  /** Drag an item from one slot to another, within a single container. */
  MoveItem: 11,
  /** A menu entry on an item, such as eating it or dropping it. */
  ItemAction: 12,
  /** Put an item on the ground. */
  DropItem: 13,
  /** Take something off. Carries the equipment slot. */
  Unequip: 14,
  /** Move items between the bag and the bank, in either direction. */
  BankMove: 15,
  /** Buy from or sell to an open shop. */
  ShopTrade: 16,
  /** Shut whatever is open, so the server stops treating the player as at it. */
  CloseInterface: 17,
  /**
   * Anything from the development tools: moderation, spawning, world edits.
   *
   * One opcode carrying a named command rather than one opcode per power. The
   * powers will keep arriving and the server checks the rank on every one of
   * them, so what matters is that they all come through the same door.
   */
  AdminCommand: 18,
  /** An answer chosen in a conversation, or closing it. */
  DialogueChoice: 20,
  /**
   * An editor action on a whole selection.
   *
   * Separate from a named command because a selection is a list, and a list does
   * not fit in a handful of named numbers.
   */
  EditorAction: 19,
  /** How somebody wants to fight: which stance the blows come from. */
  SetStance: 21,
  /** Whether being hit is answered without being told to. */
  SetRetaliate: 22,

  /** Making something at a station: which recipe, and how many of it. */
  Craft: 23,

  /** Using something on something else, which is how most things are asked for. */
  UseOn: 24,
  /** Something into the belt from the bag, or back out of it. */
  Toolbelt: 25,
  /**
   * Which quest is being followed, or none.
   *
   * Kept by the world rather than by the browser: somebody halfway through a
   * quest should still be halfway through it on another machine, and following
   * one is part of where they are up to.
   */
  TrackQuest: 26,
  /**
   * What somebody wants to look like, chosen in a window a character opened.
   *
   * Its own packet rather than a development tool, because changing your own
   * face is something every player does and the tools are for the few. The
   * world only listens while it has opened the window itself, which is what
   * stops it being a free change of appearance at any moment.
   */
  SetLook: 27,
  /**
   * Writing somebody down, or taking them off the list.
   *
   * One packet for friends and for the ignored, because they are the same act
   * with the opposite sign and a client that had two would still have to keep
   * them in step.
   */
  Friends: 28,
  /**
   * Where the editing camera is looking, when it has left the character.
   *
   * The world streams around whoever is playing, which is right until somebody
   * opens the tools and sends the camera off across the continent: then the
   * ground they are looking at is ground the server has never sent them, and
   * they are editing an empty grid. This says where to stream to instead.
   */
  Watching: 29,
  /**
   * Which kinds of request this player will hear at all.
   *
   * A setting on their screen, but the server has to know it: a request that is
   * delivered and then hidden has still interrupted somebody, and whoever sent
   * it is owed an answer rather than silence.
   */
  AllowRequests: 30,
  /**
   * Everything somebody does about a trade.
   *
   * One packet because they are all the same act with different words: ask,
   * answer, put something up, take it back, agree, and walk away. A client that
   * had six would still have to keep them in step.
   */
  Trade: 31,
  /**
   * Casting a spell: which one, and at what. Everything about whether it can be
   * cast, and what it does, is decided by the world.
   */
  CastSpell: 32,
  /** Which spell is cast at whatever is being fought, blow after blow. Nought for none. */
  SetAutocast: 33,
  /** Taking up a prayer, or putting it down. */
  TogglePrayer: 34,
  /** Reporting somebody to whoever keeps the peace. */
  ReportPlayer: 35,
  /** Everything somebody does about a party: asking, answering, leaving, pointing. */
  Party: 36,
  /** Everything somebody does about a guild: founding, asking, ranks, the notice. */
  Guild: 37,
  /** Everything somebody does at the exchange: offering, calling one off, collecting. */
  Exchange: 38,
} as const;

/** Server to client. */
export const ServerOp = {
  /** Handshake accepted, carries the shard and the player's own entity id. */
  Welcome: 1,
  /** Handshake rejected, carries a reason the client can render. */
  Reject: 2,
  Pong: 3,
  /** Everything about the local player after login. Sent once. */
  PlayerState: 4,
  /** Terrain and collision for a region the player just came into range of. */
  RegionData: 5,
  /** An entity entered view. */
  EntitySpawn: 6,
  /** An entity left view or was removed. */
  EntityDespawn: 7,
  /** Batched positions for everything in view. The most frequent packet by far. */
  EntityPositions: 8,
  /** Server's authoritative position for the local player, plus the last input it applied. */
  MoveCorrection: 9,
  Chat: 10,
  /** Experience gained in a skill. */
  SkillUpdate: 11,
  /** Run energy, prayer points, and other frequently changing local values. */
  VitalsUpdate: 12,
  /** A restart is coming. Carries the remaining seconds. */
  SystemUpdate: 13,
  /** Server is draining. The client reconnects on its own after this. */
  Disconnect: 14,
  /**
   * Names, examine text and menu options for NPCs and scenery. Sent once at login.
   * The client needs these to build a right click menu without asking the server
   * what is under the cursor every time somebody opens one.
   */
  Definitions: 15,
  /** A message from or to one named player. */
  PrivateMessage: 16,
  /**
   * Slots in one container. Full after login or a bank opening, otherwise only the
   * slots that changed, because a single item moving does not need 28 slots of wire.
   */
  ContainerUpdate: 17,
  /** Piles of items that came into view. */
  GroundItems: 18,
  /** Piles that were taken, expired, or left view. */
  GroundItemsGone: 19,
  /** What another player is wearing, when it changes. */
  Appearance: 20,
  /** The local player's summed worn bonuses. */
  Bonuses: 21,
  /** The bank opened or closed. Its contents ride on the ordinary container update. */
  BankState: 22,
  /** A shop opened, closed, or its stock changed. */
  ShopState: 23,
  /** What this player is allowed to do, and what the tools should show. */
  AdminState: 24,
  /**
   * A change to the world that everybody has to see: scenery placed or removed.
   *
   * Sent to every client in range rather than only the one that made it, because
   * a world edit that only the editor can see is not an edit to the world.
   */
  WorldPatch: 25,
  /**
   * What the tools asked about one thing in the world.
   *
   * A reply to a question rather than a stream: the editor asks about whatever it
   * has selected, and this is what the server knows about it.
   */
  AdminFacts: 26,
  /** What one character drops, for the tools. Only ever sent to an editor. */
  DropTable: 27,
  /**
   * The areas that keep characters in them.
   *
   * Only ever sent to an editor: a zone is how the world is built rather than
   * something a player is meant to be able to read off their screen.
   */
  Zones: 28,
  /**
   * Where characters are stationed, for the tools.
   *
   * The world tells a client about anything alive near it; this is the list of
   * where they come from, which is what an editor needs to find one that is
   * nowhere near anybody.
   */
  Spawns: 29,
  /** What one shop sells, for the tools. */
  ShopStock: 30,
  /** What gathering at one piece of scenery gives, for the tools. */
  Gathering: 31,
  /** A character is saying something, and what can be said back. */
  Dialogue: 33,
  /** How far along every quest is, and which flags are carried. */
  Progress: 34,
  /**
   * Everything the world was found to be wrong about.
   *
   * Sent when the tools ask, rather than continuously: it is a report on a
   * world that is being built, not a state a player has any use for.
   */
  WorldCheck: 32,
  /**
   * A blow landed, and what it left.
   *
   * Carries the damage and the health it took the target to, so a client can draw
   * the number and the bar from one packet rather than two that can disagree.
   */
  Hit: 35,
  /** Somebody died, so their body can fall over rather than blink out. */
  Died: 36,

  /** What a crafting station makes, opened when somebody reaches one. */
  CraftingState: 37,

  /**
   * Somebody is doing something worth seeing them do.
   *
   * Reaching into the water for a net, bending over a fire to turn a fish. The
   * server says who and which, because everybody nearby should see it, not only
   * the person doing it.
   */
  EntityAnimation: 38,

  /**
   * The places in a region that do something to whoever walks into them.
   *
   * For the tools only. Each is sent as the text it is written as, because the
   * conditions and effects it carries are the same shapes a conversation
   * carries and there is nothing to be gained by teaching the wire about them
   * twice.
   */
  Triggers: 39,
  /** Hold the camera on a spot for a moment, which is what a scene is. */
  HoldCamera: 40,
  /**
   * A window a character opened, and what it is for.
   *
   * The conversation is over by the time this arrives: opening a window is the
   * last thing an answer does, which is why the answer that does it has no way
   * out of its own.
   */
  OpenMenu: 41,
  /**
   * Who is written down, and which of them are here.
   *
   * Sent whole rather than as changes: the list is short, it is looked at
   * rarely, and a list that drifts out of step with the world is worse than one
   * that costs a few dozen bytes when somebody logs in.
   */
  Friends: 42,
  /**
   * Where every region sits in the one world they share.
   *
   * Rectangles and names rather than ground: enough to draw the arrangement and
   * say what lies north of what, without sending a map of everywhere to
   * somebody standing in one place.
   */
  WorldPlan: 43,
  /**
   * Something crossing the ground between two fighters.
   *
   * Sent when a blow lands from further away than an arm, so an arrow, a bolt
   * or a spark is seen going where the damage went. Drawn and forgotten: it
   * carries no state and nothing waits for it, because the blow it belongs to
   * has already happened.
   */
  Flying: 44,
  /**
   * What time it is in the world.
   *
   * Sent once when somebody arrives and again whenever a designer holds the day
   * still or lets it go. The client runs the clock itself from what this says,
   * because a sky that waits for a packet to change is a sky that stutters.
   */
  TimeOfDay: 45,
  /**
   * One square of ground: its heights, what it is made of, what blocks, and
   * everything standing on it.
   *
   * The ground used to arrive all at once with the region, which works for an
   * island and does not for a continent: a hundred and twenty eight tiles is a
   * hundred kilobytes, so three and a half thousand would be seventy six
   * megabytes in a single packet. Squares near somebody arrive instead, and the
   * rest as they walk.
   */
  GroundData: 46,
  /**
   * Squares far enough away to forget.
   *
   * Sent one ring later than they stop being sent, so walking back and forth
   * across the line between two squares does not fetch one of them over and
   * over.
   */
  GroundGone: 47,
  /**
   * A moment of black, for something that has to happen out of sight.
   *
   * A character moved to another spot while somebody is looking at them would
   * otherwise be seen to teleport. The screen dips and comes back, and by then
   * they are where they are meant to be.
   */
  Blink: 48,
  /**
   * Somebody is asking this player for something.
   *
   * A friend request or a trade request. Carries only who is asking and what
   * for: what is done about it is the client's, and answering it goes back as
   * the ordinary act for that kind of thing.
   */
  Asked: 49,
  /**
   * How a trade stands: who with, which half of it, and who has agreed.
   *
   * What is actually being offered rides on the ordinary container packets, the
   * same as a bag or a bank does, because it is the same thing and everything
   * that draws one already works.
   */
  TradeState: 50,
  /**
   * Every spell and prayer the world knows, as written in the tools.
   *
   * Sent whole at login and again whenever the tools change one. Carried as
   * the content itself rather than a wire format of its own, because the panels
   * show nearly every field and the tools edit all of them.
   */
  Spellbook: 51,
  /** Faith left, the prayers held up, the spell set to cast, and what is lasting on them. */
  Devotion: 52,
  /** The sound library and which sounds each moment plays, as JSON like the spellbook. */
  SoundLibrary: 53,
  /** A sound somebody nearby should hear: a moment by its cue, or one sound by name. */
  PlaySound: 54,
  /** The collection log: its pages, as content makes them, and what is in it. JSON. */
  Collection: 55,
  /**
   * Who is in the party, where each of them is, and where the party is headed.
   *
   * Carried as JSON: it is a handful of names sent every second or so, and the
   * panel that draws it shows nearly every field of it.
   */
  PartyState: 56,
  /** The guild somebody is in, who is in it, and anything they have been asked. JSON. */
  GuildState: 57,
  /** Somebody's own offers at the exchange, and whether the window is open. JSON. */
  ExchangeState: 58,
} as const;

export type ClientOpcode = (typeof ClientOp)[keyof typeof ClientOp];
export type ServerOpcode = (typeof ServerOp)[keyof typeof ServerOp];

/** Reasons a handshake or session can be refused, rendered by the client. */
export const RejectReason = {
  BadProtocol: 1,
  BadSession: 2,
  WorldFull: 3,
  Banned: 4,
  AlreadyOnline: 5,
  Updating: 6,
} as const;

export type RejectReasonCode = (typeof RejectReason)[keyof typeof RejectReason];

export const REJECT_MESSAGES: Readonly<Record<number, string>> = {
  [RejectReason.BadProtocol]: 'This version of the game is out of date. Reload the page.',
  [RejectReason.BadSession]: 'Your session expired. Log in again.',
  [RejectReason.WorldFull]: 'This world is full. Try again shortly.',
  [RejectReason.Banned]: 'This account cannot log in.',
  [RejectReason.AlreadyOnline]: 'That character is already logged in.',
  [RejectReason.Updating]: 'The world is restarting. Reconnecting shortly.',
};
